import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const steps = [
  {
    n: "01",
    t: "Discovery call",
    body:
      "A 30-minute conversation about what you're building, where you're stuck, and what success looks like for you. No pitch, no pressure — just clarity on whether we're a good fit.",
    note:
      "You'll leave with a clear sense of what working together looks like. I'll leave knowing whether I can genuinely help.",
  },
  {
    n: "02",
    t: "Proposal",
    body:
      "Based on our conversation, I put together a custom proposal — scope, deliverables, timeline, and investment. No generic packages. Everything is scoped to what you actually need.",
    note:
      "This is also where we catch anything unclear before it becomes a problem. Scope is agreed in writing before anything starts.",
  },
  {
    n: "03",
    t: "Brief & alignment",
    body:
      "Once we're agreed, we go deep. I gather everything I need — brand references, audience context, competitive landscape, and what's been tried before. The brief is the foundation. If it's weak, everything built on top of it is too.",
    note: "This is the step most clients want to skip. It's the one I always protect.",
  },
  {
    n: "04",
    t: "Execution",
    body:
      "Depending on the project — strategy documents, campaign creative, content, social systems — I deliver in phases with clear checkpoints so nothing comes as a surprise at the end.",
    note:
      "I work with a high degree of independence. You won't need to hand-hold the process — that's what you're hiring me for.",
  },
  {
    n: "05",
    t: "Review & delivery",
    body:
      "We review together, incorporate feedback, and finalise. You receive everything in a clean, organised handover — ready to use, not just ready to look at. Retainer clients get a monthly review call built in.",
  },
];

const faqs = [
  {
    q: "How long does a typical project take?",
    a: "It depends entirely on scope. A brand audit can be done in a week. A full brand overhaul takes 4–6 weeks. A campaign from brief to launch can be as fast as one week — as it was for Fi × Sima Taparia. Timeline is always agreed upfront in the proposal.",
  },
  {
    q: "Do you work with brands that are just starting out?",
    a: "Yes — and some of my best work has been exactly that. Building from zero is a speciality, not a stepping stone. I built Fi Money's entire brand and social presence from scratch. If you have no playbook yet, I'll help you write one.",
  },
  {
    q: "Do you take on one project at a time?",
    a: "I take on a limited number of projects each quarter to make sure every client gets the attention their brand deserves. If timing is tight, reach out early — we can plan ahead.",
  },
  {
    q: "Can agencies work with you on a confidential basis?",
    a: "Yes. I work with agencies as a whitelabel strategic partner — all work delivered under your brand. NDA is standard. Get in touch directly to discuss.",
  },
];

export default function Process() {
  usePageMeta({
    title: "Process — Shivani | How We Work Together",
    description: "A clear, collaborative process — discovery call, custom proposal, and focused execution. No generic packages, no surprises.",
    ogTitle: "Process — Shivani | How We Work Together",
    ogDescription: "Discovery, custom proposal, and focused execution. No generic packages.",
  });
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div data-testid="process-page">
      {/* INTRO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-16 md:pt-24 pb-12">
        <p className="eyebrow mb-8">Process · How we work</p>
        <h1
          className="serif-display leading-[0.92] max-w-[20ch]"
          style={{ fontSize: "clamp(2.85rem, 7vw, 6.25rem)" }}
        >
          How we <span className="serif-italic">work together.</span>
        </h1>
        <div className="grid md:grid-cols-12 mt-10">
          <p className="md:col-span-7 md:col-start-6 text-base md:text-lg leading-relaxed">
            Every project is different — but the way I work isn&apos;t. Here&apos;s what you can
            expect from the moment you reach out to the moment we wrap.
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <ul>
          {steps.map((s, i) => (
            <li
              key={s.n}
              data-testid={`process-step-${s.n}`}
              className="grid md:grid-cols-12 gap-6 md:gap-12 py-14 md:py-20 items-start"
              style={{
                borderTop: "0.5px solid rgba(26,26,26,0.35)",
                borderBottom:
                  i === steps.length - 1 ? "0.5px solid rgba(26,26,26,0.35)" : "none",
              }}
            >
              <div className="md:col-span-5">
                <span
                  className="serif-display leading-[0.85] block"
                  style={{
                    fontSize: "clamp(5rem, 11vw, 12rem)",
                    color: "rgba(26,26,26,0.85)",
                  }}
                >
                  {s.n}
                </span>
              </div>
              <div className="md:col-span-7 md:pt-6">
                <h2 className="serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.02]">
                  {s.t}
                </h2>
                <p className="mt-6 text-base md:text-lg leading-relaxed max-w-[58ch]">
                  {s.body}
                </p>
                {s.note && (
                  <div
                    className="mt-8 pt-5 max-w-[58ch]"
                    style={{ borderTop: "0.5px solid rgba(26,26,26,0.25)" }}
                  >
                    <p
                      className="eyebrow mb-2"
                      style={{ color: "rgba(26,26,26,0.55)" }}
                    >
                      Note
                    </p>
                    <p className="text-[0.95rem] leading-relaxed serif-italic">{s.note}</p>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
        data-testid="process-faq"
      >
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <p className="eyebrow mb-6" style={{ color: "rgba(26,26,26,0.55)" }}>
              FAQ
            </p>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
              Common <span className="serif-italic">questions.</span>
            </h2>
          </div>
        </div>

        <ul>
          {faqs.map((f, i) => {
            const open = openIdx === i;
            return (
              <li
                key={f.q}
                data-testid={`faq-${i + 1}`}
                style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full grid grid-cols-12 items-baseline gap-4 py-7 md:py-9 text-left transition-colors hover:bg-[#E8E2D5]"
                >
                  <span
                    className="col-span-2 md:col-span-1 eyebrow"
                    style={{ color: "rgba(26,26,26,0.5)" }}
                  >
                    0{i + 1}
                  </span>
                  <span className="col-span-9 md:col-span-9 serif-display text-2xl md:text-3xl lg:text-[2rem] leading-[1.1] pr-4">
                    {f.q}
                  </span>
                  <span className="col-span-1 md:col-span-2 flex justify-end">
                    {open ? (
                      <Minus size={26} strokeWidth={1} />
                    ) : (
                      <Plus size={26} strokeWidth={1} />
                    )}
                  </span>
                </button>
                {open && (
                  <div className="grid grid-cols-12 pb-9 md:pb-12">
                    <p className="col-span-12 md:col-span-9 md:col-start-2 text-base md:text-lg leading-relaxed max-w-[64ch]">
                      {f.a}
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="rule" />
      </section>
    </div>
  );
}
